import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export interface ProcessedLink {
  href: string;
  target?: string;
  rel?: string;
  isInternal: boolean;
  nodeId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class LinkParserService {
  private readonly DOCMAGIC_DOMAIN = 'https://www.docmagic.com';
  private readonly INTERNAL_NODE_PATTERN = /^internal:node\/(\d+)$/;
  private readonly DEPLOYED_DOMAIN = environment.appUiUrl;

  private readonly router = inject(Router);

  /**
   * Processes a link and returns the appropriate properties
   * @param originalHref - The original link
   * @returns The processed link properties
   */
  processLink(originalHref: string): ProcessedLink {
    if (!originalHref) {
      return {
        href: '#',
        isInternal: false,
      };
    }

    // Check if it's an internal link (internal:node/ID)
    const internalMatch = this.INTERNAL_NODE_PATTERN.exec(originalHref);
    if (internalMatch) {
      const nodeId = parseInt(internalMatch[1], 10);
      return {
        href: `/articles/${nodeId}`,
        isInternal: true,
        nodeId,
      };
    }

    // Check if it's a localhost link to convert
    if (originalHref.includes(this.DEPLOYED_DOMAIN)) {
      const path = originalHref.replace(this.DEPLOYED_DOMAIN, '');
      // If it's a link to an article (/articles/XXX), treat it as an internal link
      if (path.startsWith('/articles/')) {
        const nodeId = parseInt(path.replace('/articles/', ''), 10);
        return {
          href: `/articles/${nodeId}`,
          isInternal: true,
          nodeId,
        };
      }

      // Otherwise, convert to DocMagic
      return {
        href: `${this.DOCMAGIC_DOMAIN}${path}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        isInternal: false,
      };
    }

    // For all other links, treat them as external links
    return {
      href: originalHref,
      target: '_blank',
      rel: 'noopener noreferrer',
      isInternal: false,
    };
  }

  /**
   * Processes HTML content and replaces all links with their processed versions
   * @param content - The HTML content to process
   * @returns The HTML content with processed links
   */
  processContentLinks(content: string): string {
    if (!content) {
      return content;
    }

    // Replace links in <a> tags
    return content.replace(/<a\s+([^>]*?)href\s*=\s*["']([^"']*?)["']([^>]*?)>/gi, (match, beforeHref, href, afterHref) => {
      const processedLink = this.processLink(href as string);

      // Build link attributes
      let attributes = beforeHref + `href="${processedLink.href}"`;

      // Remove target="_blank" from internal links if it exists
      if (processedLink.isInternal) {
        // Remove target="_blank" from existing attributes
        attributes = attributes.replace(/\s*target\s*=\s*["'][^"']*["']/gi, '');
      } else if (processedLink.target) {
        // Add target="_blank" for external links
        attributes += ` target="${processedLink.target}"`;
      }

      if (processedLink.rel) {
        attributes += ` rel="${processedLink.rel}"`;
      }

      // Add a class for internal links for styling
      if (processedLink.isInternal) {
        attributes += ` class="internal-link"`;
      } else {
        attributes += ` class="external-link"`;
      }

      attributes += afterHref;

      return `<a ${attributes}>`;
    });
  }

  /**
   * Handles link click
   * @param event - The click event
   * @param href - The link URL
   * @param isInternal - Whether the link is internal
   * @param nodeId - The node ID (for internal links)
   */
  handleLinkClick(event: Event, href: string, isInternal: boolean, nodeId?: number): void {
    event.preventDefault();

    if (isInternal && nodeId) {
      // Internal navigation to the article
      void this.router.navigate(['/articles', nodeId]);
    } else {
      // Open external link in a new tab
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Extracts the node ID from an internal link
   * @param href - The internal link
   * @returns The node ID or null
   */
  extractNodeId(href: string): number | null {
    const match = this.INTERNAL_NODE_PATTERN.exec(href);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Checks if a link is an internal link
   * @param href - The link to check
   * @returns True if it's an internal link
   */
  isInternalLink(href: string): boolean {
    return this.INTERNAL_NODE_PATTERN.exec(href) !== null;
  }

  /**
   * Checks if a link is a localhost link to convert
   * @param href - The link to check
   * @returns True if it's a localhost link
   */
  isLocalhostLink(href: string): boolean {
    return href.includes(this.DEPLOYED_DOMAIN);
  }
}
